import * as React from "react";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FormHelperText, SxProps, Tooltip } from "@mui/material";
import { iconButtonSxProps } from "../../helpers";
import InfoIcon from '@mui/icons-material/Info';
import { useSelector } from "react-redux";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { validateInput } from "./helpers";
import { IKeyValueAny } from "../../Administrations/types";

interface IPassword {
  passSxProps?: SxProps;
  isEnabled?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  error?: boolean | undefined;
  helperText?: React.ReactNode;
  value?: any;
  label?: React.ReactNode | string;
  name?: string | undefined;
  disabledPasswordInfo?: boolean | undefined;
  required?: boolean;
  inputProps?: any | undefined;
}

const Password: React.FC<IPassword> = ({ passSxProps, onChange, onBlur, isEnabled = true, error, helperText, value, label = "Password", name = "password", required = false, disabledPasswordInfo = false, inputProps, }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dispatch = useStoreDispatch();
  const errorMsg = useSelector(errorMsgSelector);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const checkMinLength = (
    target: any,
    errorMsg: IKeyValueAny
  ): IKeyValueAny => {
    let valMsg = "";
    const minLengthAttr = target.getAttribute("minlength");

    if (minLengthAttr !== "" && minLengthAttr != null) {
      const minLength = target.minLength;
      if (target.value.length < minLength) {
        valMsg = `Minimum ${minLength} characters`;
        return { ...errorMsg, [target.name]: valMsg };
      }
    }
    return errorMsg;
  };
  const checkPattern = (
    target: any,
    errorMsg: IKeyValueAny
  ): IKeyValueAny => {
    let valMsg = "";
    const pattern = target.getAttribute("pattern");

    if (pattern) {
      const regex = new RegExp(pattern);
      if (!regex.test(target.value)) {
        valMsg =
          target.getAttribute("patternmsg") ||
          `Invalid input. Please enter a valid ${target.name}`;
        return { ...errorMsg, [target.name]: valMsg };
      }
    }
    return errorMsg;
  };

  const validateField = (
    target: any,
    errorMsg: IKeyValueAny
  ): IKeyValueAny => {
    // 1️⃣ Run regex first
    let updatedErr = checkPattern(target, errorMsg);

    // if regex failed, don't check minLength
    if (updatedErr[target.name]) return updatedErr;

    // 2️⃣ Run minLength only if regex passed
    updatedErr = checkMinLength(target, updatedErr);

    return updatedErr;
  };

  const onBlurHandler = (event: any) => {
    const updatedErr = validateField(event.target, errorMsg);
    dispatch(setErrorMsg(updatedErr));
    onBlur && onBlur(event);
  };


  const handleInput = (event: any) => {
    const { name } = event.target;
    // remove error for current field
    let errmsg: IKeyValueAny =
      Object.fromEntries(Object.entries(errorMsg).filter(([key]) => key !== name)) ||
      {};

    // Run validation
    const updatedErr = validateField(event.target, errmsg);
    dispatch(setErrorMsg(updatedErr));
    event.target.setCustomValidity("");
  };

  React.useEffect(() => {
    if (error) {
      inputRef?.current?.focus()
    }
  }, [error])

  const passwordPolicy = `
        The password must be at least 8 characters long and include at least one uppercase letter(A - Z), one digit(1 - 9), and one special non - alphabetic character(e.g., #@%).`;
  const disabledColor = !isEnabled ? "var(--color-disabled)" : "";

  return (
    <FormControl sx={{ ...passSxProps, width: "100%" }} variant="outlined">
      <InputLabel
        sx={{ color: disabledColor }}
        htmlFor="outlined-adornment-password"
        required={required}
        error={!!errorMsg?.[name] || error}
      >
        {label}
      </InputLabel>
      <OutlinedInput
        id="outlined-adornment-password"
        type={showPassword ? "text" : "password"}
        name={name}
        value={value}
        inputRef={inputRef}
        autoFocus={!!helperText}
        onChange={onChange}
        onBlur={onBlurHandler}
        onInput={handleInput}
        disabled={!isEnabled}
        required={required}
        error={!!errorMsg?.[name] || error}
        inputProps={inputProps}
        endAdornment={
          <InputAdornment position="end">
            <IconButton sx={iconButtonSxProps} edge="end" aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
              {showPassword ? <VisibilityOff sx={{ color: disabledColor }} /> : <Visibility sx={{ color: disabledColor }} />}
            </IconButton>
          </InputAdornment>
        }
        label={label}
      />
      <FormHelperText
        sx={{
          color: (!!errorMsg?.[name] || error) ? "var(--color-destructive)" : "",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center"
        }}
      >
        {errorMsg?.[name] ?? helperText}
        {(!!errorMsg?.[name] || error) && !disabledPasswordInfo && (
          <Tooltip title={inputProps?.passwordpolicytitle ?? passwordPolicy} arrow sx={{ maxWidth: 'none' }}>
            <InfoIcon sx={{ ml: 2 }} fontSize="inherit" color="error" />
          </Tooltip>
        )}
      </FormHelperText>
    </FormControl>
  );
};

export default Password;
